import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { BookOpen, ArrowRight, ArrowLeft } from "lucide-react";
import { useTranslations } from "../hooks/useTranslations";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { SEOHead } from "./SEOHead";
import {
  getAllArticles,
  getArticleBySlug,
  getBlogArticlePath,
  type BlogArticle,
} from "../blog/articles";
import { getLocalizedPath, getBlogArticleSlugFromUrlPath, type LocalizedBasePath } from "../utils/localizedRoutes";

export function BlogPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, currentLanguage } = useTranslations();
  const blog = t.blog;
  const articleSlug = getBlogArticleSlugFromUrlPath(location.pathname);
  const selectedArticle = articleSlug ? getArticleBySlug(articleSlug, currentLanguage) : null;
  const articles = getAllArticles(currentLanguage);

  const buildUrl = (path: LocalizedBasePath) => getLocalizedPath(path, currentLanguage);

  const formatText = (text: string): React.ReactNode => {
    const lines = text.split("\n").filter((line) => line.trim());

    if (lines.length > 1) {
      return (
        <>
          {lines.map((line, idx) => (
            <React.Fragment key={idx}>
              {line.trim()}
              {idx < lines.length - 1 && <br />}
            </React.Fragment>
          ))}
        </>
      );
    }

    return text;
  };

  const isHeading = (text: string): boolean => {
    if (text.match(/^[🌑👾📌🔹]/)) {
      return true;
    }
    if (text.length < 80 && text.endsWith(":")) {
      return true;
    }
    return false;
  };

  const openArticle = (article: BlogArticle) => {
    navigate(getBlogArticlePath(article, currentLanguage));
  };

  if (articleSlug && !selectedArticle) {
    return (
      <>
        <SEOHead
          overrideSeo={{
            title: `${blog.articleNotFound} | VEVI Monster`,
            description: blog.articleNotFound,
            keywords: "vevi monster blog",
          }}
          canonicalPathOverride={buildUrl("/blog")}
          canonicalPathIsLocalized={true}
        />
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-blue-800" style={{ paddingTop: "140px" }}>
          <div className="container mx-auto px-4 py-12">
            <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
              <CardContent className="p-8 text-center">
                <h1 className="text-gray-900 text-2xl mb-4">{blog.articleNotFound}</h1>
                <button
                  onClick={() => navigate(buildUrl("/blog"))}
                  className="text-purple-600 hover:text-purple-700"
                >
                  {blog.backToBlog}
                </button>
              </CardContent>
            </Card>
          </div>
          <Footer onShowBlog={() => navigate(buildUrl("/blog"))} />
        </div>
      </>
    );
  }

  if (selectedArticle) {
    return (
      <>
        <SEOHead
          overrideSeo={{
            title: selectedArticle.seoTitle,
            description: selectedArticle.seoDescription,
            keywords: `vevi monster blog, ${selectedArticle.slug.replace(/-/g, ", ")}`,
          }}
          canonicalPathOverride={getBlogArticlePath(selectedArticle, currentLanguage)}
          canonicalPathIsLocalized={true}
        />
        <Header />
        <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-blue-800" style={{ paddingTop: "140px" }}>
          <div className="container mx-auto px-4 py-12">
            <button
              onClick={() => navigate(buildUrl("/blog"))}
              className="flex items-center text-white hover:text-purple-200 mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              <span>{blog.backToBlog}</span>
            </button>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                  <BookOpen className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-white text-4xl">{selectedArticle.title}</h1>
                </div>
              </div>
            </div>

            <Card className="bg-white/95 backdrop-blur-sm shadow-xl">
              <CardContent className="p-8 space-y-6">
                {selectedArticle.metadata && Object.keys(selectedArticle.metadata).length > 0 && (
                  <div className="bg-gray-50 p-6 rounded-lg mb-6">
                    <h2 className="text-gray-800 text-lg font-semibold mb-4">{blog.characteristics}</h2>
                    <div className="space-y-2">
                      {Object.entries(selectedArticle.metadata).map(([key, value]) => (
                        <div key={key} className="flex">
                          <strong className="text-gray-800 min-w-[200px]">{key}:</strong>
                          <span className="text-gray-700">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {selectedArticle.content.map((paragraph, index) => {
                    const text = paragraph.trim();

                    if (!text) {
                      return null;
                    }

                    const isHeadingText = isHeading(text);
                    const isKeyValue = text.match(/^[^:]+:\s/);

                    if (isHeadingText) {
                      return (
                        <h2 key={index} className="text-gray-900 text-2xl mb-4 mt-6 font-semibold">
                          {text}
                        </h2>
                      );
                    }

                    if (isKeyValue) {
                      const [key, ...valueParts] = text.split(":");
                      const value = valueParts.join(":").trim();
                      return (
                        <div key={index} className="mb-2">
                          <strong className="text-gray-800">{key}:</strong>{" "}
                          <span className="text-gray-700">{value}</span>
                        </div>
                      );
                    }

                    return (
                      <p key={index} className="text-gray-700 leading-relaxed">
                        {formatText(text)}
                      </p>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            <Footer onShowBlog={() => navigate(buildUrl("/blog"))} />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        overrideSeo={{
          title: `${blog.title} | VEVI Monster`,
          description: blog.subtitle,
          keywords: "vevi monster blog, ai dubbing blog, video localization blog",
        }}
        canonicalPathOverride={buildUrl("/blog")}
        canonicalPathIsLocalized={true}
      />
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-blue-800" style={{ paddingTop: "140px" }}>
        <div className="container mx-auto px-4 py-12">
          <button
            onClick={() => navigate(buildUrl("/"))}
            className="flex items-center text-white hover:text-purple-200 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            <span>На главную</span>
          </button>

          <div className="mb-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-lg flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-white text-4xl">{blog.title}</h1>
                <p className="text-purple-100">{blog.subtitle}</p>
              </div>
            </div>
          </div>

          <div>
            {articles.map((article) => (
              <Card
                key={article.id}
                className="bg-white/95 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-shadow cursor-pointer mb-12 last:mb-0"
                onClick={() => openArticle(article)}
              >
                <CardContent className="p-8">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h2 className="text-gray-900 text-2xl mb-3 font-semibold">
                        {article.title}
                      </h2>

                      {article.metadata && Object.keys(article.metadata).length > 0 && (
                        <div className="mb-4 space-y-1">
                          {Object.entries(article.metadata).slice(0, 3).map(([key, value]) => (
                            <div key={key} className="text-sm text-gray-600">
                              <strong className="text-gray-800">{key}:</strong> {value}
                            </div>
                          ))}
                        </div>
                      )}

                      <p className="text-gray-700 leading-relaxed mb-4">
                        {article.excerpt}
                      </p>

                      <div className="flex items-center text-purple-600 hover:text-purple-700 transition-colors">
                        <span className="text-sm font-medium">{blog.readMore}</span>
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
        <Footer onShowBlog={() => navigate(buildUrl("/blog"))} />
      </div>
    </>
  );
}
